// Черновики сообщений: по ключу пользователь + чат + комната (null → «main»), в localStorage.
// Хранится только текст (контекст ответа не сохраняем). У каждого пользователя не более MAX_PER_USER
// черновиков — самые давние вытесняются; при выходе из аккаунта черновики этого пользователя удаляются.
const PREFIX = 'hc_draft:';
const MAX_PER_USER = 50;

interface Stored {
  text: string;
  ts: number;
}

const subs = new Set<() => void>();
let version = 0;

function notify() {
  version++;
  subs.forEach((f) => f());
}

export function draftKey(userId: string, chatId: string, roomId: string | null | undefined): string {
  return `${PREFIX}${userId}:${chatId}:${roomId ?? 'main'}`;
}

function read(key: string): Stored | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const v = JSON.parse(raw) as Stored;
    return typeof v.text === 'string' && typeof v.ts === 'number' ? v : null;
  } catch {
    return null;
  }
}

function userKeys(userId: string): string[] {
  const out: string[] = [];
  try {
    const start = `${PREFIX}${userId}:`;
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(start)) out.push(k);
    }
  } catch {
    /* хранилище недоступно */
  }
  return out;
}

export function getDraft(userId: string, chatId: string, roomId: string | null | undefined): string {
  return read(draftKey(userId, chatId, roomId))?.text ?? '';
}

/** Пустой/пробельный текст удаляет черновик. */
export function setDraft(userId: string, chatId: string, roomId: string | null | undefined, text: string) {
  const key = draftKey(userId, chatId, roomId);
  try {
    if (!text.trim()) {
      if (localStorage.getItem(key) == null) return;
      localStorage.removeItem(key);
    } else {
      const prev = read(key);
      if (prev?.text === text) return;
      localStorage.setItem(key, JSON.stringify({ text, ts: Date.now() } satisfies Stored));
      const keys = userKeys(userId);
      if (keys.length > MAX_PER_USER) {
        keys
          .map((k) => ({ k, ts: read(k)?.ts ?? 0 }))
          .sort((a, b) => a.ts - b.ts)
          .slice(0, keys.length - MAX_PER_USER)
          .forEach(({ k }) => localStorage.removeItem(k));
      }
    }
  } catch {
    return; // переполнено/недоступно — черновик просто не сохранится
  }
  notify();
}

/** Самый свежий непустой черновик чата среди основной ленты и всех комнат — для списка чатов. */
export function chatDraft(userId: string, chatId: string): string {
  const start = `${PREFIX}${userId}:${chatId}:`;
  let best: Stored | null = null;
  for (const k of userKeys(userId)) {
    if (!k.startsWith(start)) continue;
    const v = read(k);
    if (v && v.text.trim() && (!best || v.ts > best.ts)) best = v;
  }
  return best?.text ?? '';
}

export function clearUserDrafts(userId: string) {
  try {
    userKeys(userId).forEach((k) => localStorage.removeItem(k));
  } catch {
    /* хранилище недоступно */
  }
  notify();
}

export function subscribeDrafts(f: () => void): () => void {
  subs.add(f);
  return () => subs.delete(f);
}

export function draftsVersion(): number {
  return version;
}
